import {
  cacheKeys,
  FIND_ACCOUNT_ASSIGNMENTS,
  UPDATE_ASSET_ACCOUNTS,
  useCurrentWorkspace,
  useCustomMutation,
  useCustomQuery,
} from '@gripp/shared-logic';
import {
  Colors,
  LoadingIndicator,
  SaveFormTop,
  SearchInput,
  TopModalBar,
  useGetDimensions,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { EditAccountAssetsList } from './editAccountAssetsList';

type AccountAssetAssignmentFormProps = {
  accountId: string;
  title: string;
  onComplete: () => void;
};

export const AccountAssetAssignmentForm = ({
  accountId,
  title,
  onComplete,
}: AccountAssetAssignmentFormProps) => {
  const { t } = useTranslation();
  const currentWorkspace = useCurrentWorkspace(true);
  const queryClient = useQueryClient();
  const { dimensions, onLayout } = useGetDimensions();

  const [keyword, setKeyword] = useState('');

  // Load assignments upfront — same pattern as groupAssignmentForm / featureAssignmentForm
  const { data: assignmentsData, isPending: isAssignmentsLoading } =
    useCustomQuery({
      query: FIND_ACCOUNT_ASSIGNMENTS,
      cacheKey: ['account', 'assignments', accountId],
      variables: {
        accountId,
        workspaceId: currentWorkspace!.id,
      },
    });

  const [originalAssetAssignments, setOriginalAssetAssignments] =
    useState<string[]>();
  const [assetAssignments, setAssetAssignments] = useState<string[]>();

  useEffect(() => {
    if (!assignmentsData) return;

    const assets =
      assignmentsData?.account?.findAccountAssignments?.assets || [];

    setOriginalAssetAssignments(assets);
    setAssetAssignments(assets);
  }, [assignmentsData]);

  const { mutateAsync: updateAssetAccounts } = useCustomMutation({
    query: UPDATE_ASSET_ACCOUNTS,
  });

  const height = dimensions?.height ?? 0;
  const tableResultsBlockHeight = 50;
  const listMaxHeight = height - tableResultsBlockHeight;

  const handleSubmit = async () => {
    if (!originalAssetAssignments || !assetAssignments) return;

    const addedAssetIds = assetAssignments.filter(
      (id) => !originalAssetAssignments.includes(id)
    );
    const removedAssetIds = originalAssetAssignments.filter(
      (id) => !assetAssignments.includes(id)
    );

    if (addedAssetIds.length > 0) {
      await updateAssetAccounts({
        input: {
          assetIds: addedAssetIds,
          addedAccountIds: [accountId],
          removedAccountIds: [],
          workspaceId: currentWorkspace!.id,
        },
      });
    }

    if (removedAssetIds.length > 0) {
      await updateAssetAccounts({
        input: {
          assetIds: removedAssetIds,
          addedAccountIds: [],
          removedAccountIds: [accountId],
          workspaceId: currentWorkspace!.id,
        },
      });
    }

    await queryClient.invalidateQueries({
      queryKey: cacheKeys('account').all,
    });
    await queryClient.invalidateQueries({
      queryKey: cacheKeys('asset').all,
    });

    onComplete();
  };

  const isDirty = () => {
    if (!originalAssetAssignments || !assetAssignments) return false;
    return (
      originalAssetAssignments.length !== assetAssignments.length ||
      originalAssetAssignments.some((id) => !assetAssignments.includes(id)) ||
      assetAssignments.some((id) => !originalAssetAssignments.includes(id))
    );
  };

  return (
    <SaveFormTop
      onSubmit={handleSubmit}
      title={title}
      topBar={TopModalBar}
      onCancel={onComplete}
      isDirty={isDirty()}
    >
      {isAssignmentsLoading ? (
        <LoadingIndicator flexContainer />
      ) : (
        <View style={styles.container}>
          <SearchInput
            value={keyword}
            onChangeText={setKeyword}
            onDebounce={setKeyword}
            debounceLength={3}
            backgroundColor={Colors.white}
            placeholder={t('groups.assignments.assetSearchPlaceholder')}
            height={44}
          />

          <View style={styles.assignmentContainer} onLayout={onLayout}>
            <View style={styles.listContainer}>
              {assetAssignments && currentWorkspace?.id && (
                <EditAccountAssetsList
                  accountId={accountId}
                  workspaceId={currentWorkspace.id}
                  maxHeight={listMaxHeight}
                  keyword={keyword}
                  assignments={assetAssignments}
                  updateAssignments={setAssetAssignments}
                />
              )}
            </View>
          </View>
        </View>
      )}
    </SaveFormTop>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 20,
  },
  assignmentContainer: {
    flex: 1,
    marginTop: 20,
  },
  listContainer: {
    borderRadius: 12,
    overflow: 'hidden',
  },
});
